import React, { useState, useEffect } from 'react';
import { Download, Maximize2, Minimize2, Loader2, X } from 'lucide-react';
import { PDFDocument, rgb, degrees, StandardFonts } from 'pdf-lib';
import configService from '../services/configService';

const WatermarkedPDFPreview = ({ 
  pdfUrl, 
  watermarkInfo, 
  watermarkEnabled = true,
  watermarkFontSize: propFontSize, // Accept font size as prop
  fileName = 'document.pdf',
  onClose,
  onDownload,
  hideDownloadButton = false 
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false); // Default to normal preview
  const [downloading, setDownloading] = useState(false);
  const [watermarkedPdfUrl, setWatermarkedPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [watermarkFontSize, setWatermarkFontSize] = useState(propFontSize || 14); // Use prop or default

  // Generate watermark text
  const watermarkText = watermarkInfo ? 
    `${watermarkInfo.userName} • ${watermarkInfo.userEmail}` : 
    'CONFIDENTIAL';
  const secondaryText = watermarkInfo ? 
    `${watermarkInfo.timestamp} • ${watermarkInfo.dataroomId}` : 
    '';

  // Fetch watermark font size on mount only if not provided as prop
  useEffect(() => {
    if (!propFontSize) {
      const fetchFontSize = async () => {
        try {
          const response = await configService.getPublicConfig('WATERMARK_FONT_SIZE');
          const fontSize = parseInt(response?.data?.value || response?.value || '14');
          setWatermarkFontSize(Math.min(Math.max(fontSize, 8), 72));
        } catch (error) {
          setWatermarkFontSize(14);
        }
      };
      fetchFontSize();
    } else {
      setWatermarkFontSize(propFontSize);
    }
  }, [propFontSize]);

  // Apply watermark to PDF and create preview URL
  useEffect(() => {
    const applyWatermark = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // If watermark is disabled, use original PDF URL directly
        if (!watermarkEnabled) {
          setWatermarkedPdfUrl(pdfUrl);
          setLoading(false);
          return;
        }
        
        
        // Fetch the PDF
        const response = await fetch(pdfUrl);
        if (!response.ok) {
          throw new Error('Failed to fetch PDF');
        }
        
        const pdfArrayBuffer = await response.arrayBuffer();
        
        // Apply watermark
        const pdfDoc = await PDFDocument.load(pdfArrayBuffer, {
          ignoreEncryption: true
        });
        
        // Embed fonts
        const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
        const regularFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
        
        const pages = pdfDoc.getPages();
        
        // Apply watermark to each page
        pages.forEach((page, pageIndex) => {
          const { width, height } = page.getSize();
          
          // Simplified watermark text - only user info and timestamp
          const userName = watermarkInfo?.userName || 'User';
          const userEmail = watermarkInfo?.userEmail || 'user@example.com';
          const currentDateTime = new Date().toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
          });
          
          // Combine info into single watermark text
          const watermarkLine = `${userName} • ${userEmail} • ${currentDateTime}`;
          
          // Pattern configuration - bottom-left to top-right (positive rotation)
          const horizontalSpacing = 250;
          const verticalSpacing = 100;
          const rotation = 45; // Positive for bottom-left to top-right
          
          // Calculate grid
          const cols = Math.ceil(width / horizontalSpacing) + 2;
          const rows = Math.ceil(height / verticalSpacing) + 2;
          
          // Draw diagonal pattern from bottom-left to top-right
          for (let row = -1; row < rows; row++) {
            for (let col = -1; col < cols; col++) {
              const x = col * horizontalSpacing;
              const y = row * verticalSpacing;
              
              // Draw watermark text with configured font size
              page.drawText(watermarkLine, {
                x: x,
                y: y,
                size: watermarkFontSize,
                font: regularFont,
                color: rgb(0.4, 0.4, 0.4),
                opacity: 0.2,
                rotate: degrees(rotation)
              });
            }
          }
        });
        
        // Save watermarked PDF
        const pdfBytes = await pdfDoc.save();
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        
        setWatermarkedPdfUrl(url);
        setLoading(false);
        
        // Notify parent component that watermarked PDF is ready
        if (onDownload) {
          onDownload(url);
        }
        
        
      } catch (error) {
        setError(error.message);
        setLoading(false);
      }
    };
    
    applyWatermark();
    
    // Cleanup
    return () => {
      if (watermarkedPdfUrl) {
        URL.revokeObjectURL(watermarkedPdfUrl);
      }
    };
  }, [pdfUrl, watermarkEnabled, watermarkFontSize]); // Add watermarkFontSize to dependencies

  // Handle fullscreen
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Download watermarked PDF
  const handleDownload = async () => {
    try {
      setDownloading(true);
      
      if (watermarkedPdfUrl) {
        // Use the already watermarked PDF
        const link = document.createElement('a');
        link.href = watermarkedPdfUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
    } catch (error) {
      alert('Failed to download watermarked PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  // Allow right-click on PDF for copy functionality
  useEffect(() => {
    // Context menu allowed
    // const handleContextMenu = (e) => {
    //   e.preventDefault();
    //   return false;
    // };
    // 
    // document.addEventListener('contextmenu', handleContextMenu);
    // return () => document.removeEventListener('contextmenu', handleContextMenu);
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[600px]">
        <Loader2 className="w-12 h-12 animate-spin text-primary-600 mb-4" />
        <p className="text-gray-600">Applying watermark to PDF...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[600px]">
        <div className="text-red-500 mb-4">Error: {error}</div>
        <p className="text-gray-600">Failed to load watermarked PDF</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${isFullscreen ? 'fixed inset-0 z-[9999] bg-white' : 'h-full'}`} style={{ height: isFullscreen ? '100vh' : '100%', minHeight: isFullscreen ? '100vh' : '70vh' }}>
      {/* Toolbar with fullscreen and close buttons */}
      <div className="bg-gray-800 text-white border-b border-gray-700 px-4 py-3 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">
            PDF Viewer {isFullscreen ? '(Fullscreen Mode)' : ''}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* Fullscreen toggle button */}
          <button
            onClick={toggleFullscreen}
            className="p-2 bg-gray-700 hover:bg-gray-600 rounded transition-colors flex items-center gap-2"
            title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {isFullscreen ? (
              <>
                <Minimize2 className="w-5 h-5" />
                <span className="text-sm">Exit Fullscreen</span>
              </>
            ) : (
              <>
                <Maximize2 className="w-5 h-5" />
                <span className="text-sm">Fullscreen</span>
              </>
            )}
          </button>

          {/* Close button - always visible */}
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 bg-red-600 hover:bg-red-700 rounded transition-colors flex items-center gap-2"
              title="Close PDF Viewer"
            >
              <X className="w-5 h-5" />
              <span className="text-sm">Close</span>
            </button>
          )}
        </div>
      </div>
      
      {/* PDF viewer - displays the watermarked PDF with native controls */}
      <div className="flex-1 relative overflow-hidden bg-gray-50" style={{ minHeight: isFullscreen ? 'calc(100vh - 60px)' : 'calc(70vh - 60px)' }}>
        {watermarkedPdfUrl && (
          <iframe
            src={`${watermarkedPdfUrl}#toolbar=1&navpanes=0&scrollbar=1&view=FitW&zoom=100`}
            className="w-full h-full border-0"
            title="PDF Viewer"
            style={{
              width: '100%',
              height: '100%',
              minHeight: isFullscreen ? 'calc(100vh - 60px)' : 'calc(70vh - 60px)',
              border: 'none',
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              return false;
            }}
          />
        )}
      </div>
      
      {/* Disable print CSS */}
      <style jsx>{`
        @media print {
          body * {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default WatermarkedPDFPreview;